import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Select, Prompt } from "@/shared/ui";
import { extractErrorMessage, type Locale } from "@/shared/utils";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { useUpdateProjectMutation, useGetProjectAuditQuery, ProjectHistoryModal } from "@/features/projects";
import { PROJECT_STATUS_MAP } from "@/features/projects/utils/consts";
import type { ProjectStatus, AuditQueryParams } from "@/features/projects";
import { AuditEntryItem } from "@/shared/components";
import { buildTasksAuditConfig, isReorderEntry } from "../../../utils/auditHelpers";
import type { BoardProject } from "../../../types/api";

interface Props {
  project: BoardProject;
  locale: Locale;
}

const STATUS_OPTIONS: ProjectStatus[] = ["planned", "active", "paused", "closed"];

export default function ProjectOverviewSidebar({ project, locale }: Props) {
  const { t } = useTranslation("BoardPage");
  const { t: tProjects } = useTranslation("ProjectsPage");
  const { t: tTasks } = useTranslation("TasksPage");
  const { hasPermission } = useAuth();

  const auditConfig = useMemo(() => buildTasksAuditConfig(tTasks, tProjects, locale), [tTasks, tProjects, locale]);
  const canEdit = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);

  const [updateProject] = useUpdateProjectMutation();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const auditParams = useMemo<AuditQueryParams>(
    () => ({ page_size: 10, project: project.id }),
    [project.id],
  );
  const { data: auditData, refetch: refetchAudit } = useGetProjectAuditQuery(auditParams);

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((status) => ({
        value: status,
        label: tProjects(PROJECT_STATUS_MAP[status].label),
      })),
    [tProjects],
  );

  const statusInfo = PROJECT_STATUS_MAP[project.status];

  async function handleStatusChange(value: string | null) {
    if (!value || value === project.status || isUpdatingStatus) return;
    setIsUpdatingStatus(true);
    try {
      await updateProject({ id: project.id, data: { status: value as ProjectStatus } }).unwrap();
      setTimeout(() => refetchAudit(), 1000);
      setPrompt({
        title: t("overview.sidebar.statusSuccessTitle"),
        text: t("overview.sidebar.statusSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("overview.sidebar.statusErrorTitle"),
        text: extractErrorMessage(err, "overview.sidebar.statusErrorText", t),
        variant: "error",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  const auditEntries = (auditData?.results ?? []).filter((e) => !isReorderEntry(e));

  return (
    <>
      <div className="p-7 radius-3xl surface-base-fill">
        <div className="flex justify-between items-center gap-3">
          <span className={`text-body-bold-lg content-action-${statusInfo.color}`}>{tProjects(statusInfo.label)}</span>
          {canEdit && (
            <div className="flex justify-end">
              <Select
                options={statusOptions}
                value={null}
                onChange={handleStatusChange}
                placeholder={t("overview.sidebar.updateStatus")}
                disabled={isUpdatingStatus}
              />
            </div>
          )}
        </div>

        <div className="border-t surface-base-stroke my-4" />

        <div className="flex items-center justify-between mb-5">
          <h3 className="text-body-bold-md content-base-primary">{t("overview.sidebar.history")}</h3>
          {auditEntries.length > 0 && (
            <button
              type="button"
              className="text-label-sm content-action-brand cursor-pointer"
              onClick={() => setShowHistory(true)}>
              {t("overview.sidebar.showAll")}
            </button>
          )}
        </div>

        {auditEntries.length === 0 ? (
          <p className="text-body-regular-sm content-base-low">{t("overview.sidebar.emptyHistory")}</p>
        ) : (
          <div className="flex flex-col">
            {auditEntries.slice(0, 5).map((entry, index) => (
              <AuditEntryItem
                key={entry.id}
                entry={entry}
                isLast={index === auditEntries.slice(0, 5).length - 1}
                namespace="history"
                config={auditConfig}
                t={tTasks}
                locale={locale}
              />
            ))}
          </div>
        )}
      </div>

      {showHistory && (
        <ProjectHistoryModal projectId={project.id} onClose={() => setShowHistory(false)} locale={locale} />
      )}

      {prompt && (
        <Prompt
          onClose={() => setPrompt(null)}
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          namespace="BoardPage"
        />
      )}
    </>
  );
}

